import type { PaginationState } from '@tanstack/react-table';
import { atom } from 'jotai';
import { z } from 'zod';
import { paramsAtom, SearchParam } from './searchParams';

export const pageIndexSchema = z.coerce.number().int().positive().catch(0);
const defaultPageSizes = [10, 25, 50, 100] as const;
export const createPageSizeSchema = (pageSizes: readonly number[]) => {
  const sortedPageSizes = [...pageSizes].sort((a, b) => a - b);

  return z.coerce
    .number()
    .int()
    .catch(sortedPageSizes[0])
    .transform(
      (value) =>
        sortedPageSizes.findLast((pageSize) => pageSize <= value) ??
        sortedPageSizes[0],
    );
};

export const pageSizeSchema = createPageSizeSchema(defaultPageSizes);

export const createPaginationAtom = (pageSizes: readonly number[]) => {
  const pageSizeSchema = createPageSizeSchema(pageSizes);

  return atom<PaginationState, [PaginationState], PaginationState>(
    (get) => {
      const searchParams = get(paramsAtom);

      const pageIndex = pageIndexSchema.parse(
        searchParams?.get(SearchParam.pageIndex),
      );
      const pageSize = pageSizeSchema.parse(
        searchParams?.get(SearchParam.pageSize),
      );

      return { pageIndex, pageSize };
    },
    (_get, set, paginationState) => {
      set(paramsAtom, (searchParams) => {
        const params = new URLSearchParams(searchParams);

        const pageIndex = pageIndexSchema.parse(paginationState.pageIndex);
        const pageSize = pageSizeSchema.parse(paginationState.pageSize);

        if (pageIndex) {
          params.set(SearchParam.pageIndex, pageIndex.toString());
        } else {
          params.delete(SearchParam.pageIndex);
        }

        params.set(SearchParam.pageSize, pageSize.toString());

        return params;
      });

      return paginationState;
    },
  );
};

export const paginationAtom = createPaginationAtom(defaultPageSizes);
